import { Box, Tooltip, Typography, useTheme } from '@mui/material'
import type { NotificationChannelStatus } from '@/services/notificationService'
import { describeNotificationChannel } from '@/services/notificationChannels'
import {
  byDeliveryProgress,
  describeNotificationStatus,
} from '@/services/notificationStatuses'
import NotificationStatusChip from './NotificationStatusChip'

type Props = {
  channels: NotificationChannelStatus[]
  /** One 24px icon per channel with no chips; the tooltip carries the statuses. */
  plain?: boolean
}

const byChannel = (channels: NotificationChannelStatus[]) => {
  const groups = new Map<string, NotificationChannelStatus[]>()
  for (const entry of channels) {
    groups.set(entry.channel, [...(groups.get(entry.channel) ?? []), entry])
  }
  return [...groups].map(([channel, statuses]) => ({
    channel,
    statuses: [...statuses].sort((a, b) =>
      byDeliveryProgress(a.status, b.status)
    ),
  }))
}

const withCount = ({ status, count, lastError }: NotificationChannelStatus) => {
  const { label } = describeNotificationStatus(status)
  const counted = count > 1 ? `${label} ×${count}` : label
  return lastError ? `${counted} (${lastError})` : counted
}

/**
 * One entry per channel an alert's messages went to: the furthest status any
 * of them reached there, and how many more messages on that channel are in
 * other states. The tooltip and the accessible name list every status.
 */
const NotificationChannelStatuses = ({ channels, plain = false }: Props) => {
  const darkMode = useTheme().palette.mode === 'dark'
  if (!channels.length) return <>-</>
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: plain ? 1.5 : 1,
        rowGap: 0.5,
        justifyContent: plain ? 'center' : 'flex-start',
        width: '100%',
      }}
    >
      {byChannel(channels).map(({ channel, statuses }) => {
        const { label, icon } = describeNotificationChannel(channel)
        const [furthest, ...others] = statuses
        const otherCount = others.reduce((sum, entry) => sum + entry.count, 0)
        const summary = `${label}: ${statuses.map(withCount).join(', ')}`
        return (
          <Tooltip key={channel} title={<Box dir="rtl">{summary}</Box>}>
            <Box
              aria-label={summary}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              {icon ? (
                <img
                  src={icon}
                  alt={label}
                  style={{
                    width: plain ? 24 : 18,
                    height: plain ? 24 : 18,
                    filter: darkMode ? 'brightness(0) invert(1)' : undefined,
                  }}
                />
              ) : (
                <Typography variant="caption">{label}</Typography>
              )}
              {!plain && (
                <>
                  <NotificationStatusChip status={furthest.status} dense />
                  {furthest.count > 1 && (
                    <Typography variant="caption" dir="ltr">
                      ×{furthest.count}
                    </Typography>
                  )}
                  {otherCount > 0 && (
                    <Typography variant="caption" dir="ltr">
                      +{otherCount}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Tooltip>
        )
      })}
    </Box>
  )
}

export default NotificationChannelStatuses
